'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import NavBar from '@/components/NavBar';
import HomeFooter from '@/components/HomeFooter';
import EmailAuthModal from '@/components/EmailAuthModal';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

interface CreatorVideo {
  id: string;
  title: string;
  category: string;
  video_status: string;
  views: number;
  likes_count: number;
  comments_count: number;
  created_at: string;
  thumbnail: string;
  thumbnail_alt: string;
}

interface TipRecord {
  id: string;
  video_id: string;
  amount: number;
  payment_status: string;
  tipper_name: string | null;
  message: string | null;
  created_at: string;
}

interface PayoutRecord {
  id: string;
  amount: number;
  currency: string;
  status: 'pending' | 'processing' | 'paid' | 'failed' | 'cancelled';
  stripe_transfer_id: string | null;
  stripe_payout_id: string | null;
  payout_method: string | null;
  notes: string | null;
  requested_at: string;
  processed_at: string | null;
  created_at: string;
}

interface VideoTipSummary {
  video_id: string;
  title: string;
  thumbnail: string;
  thumbnail_alt: string;
  total_tips: number;
  tip_count: number;
}

interface DailyEarning {
  date: string;
  amount: number;
  count: number;
}

const STATUS_COLORS: Record<string, string> = {
  approved: 'bg-green-500 bg-opacity-20 text-green-400 border border-green-500 border-opacity-30',
  pending: 'bg-yellow-500 bg-opacity-20 text-yellow-400 border border-yellow-500 border-opacity-30',
  rejected: 'bg-red-500 bg-opacity-20 text-red-400 border border-red-500 border-opacity-30',
};

const PAYOUT_STATUS_STYLES: Record<string, string> = {
  pending: 'bg-yellow-500 bg-opacity-20 text-yellow-400 border border-yellow-500 border-opacity-30',
  processing: 'bg-blue-500 bg-opacity-20 text-blue-400 border border-blue-500 border-opacity-30',
  paid: 'bg-green-500 bg-opacity-20 text-green-400 border border-green-500 border-opacity-30',
  failed: 'bg-red-500 bg-opacity-20 text-red-400 border border-red-500 border-opacity-30',
  cancelled: 'bg-gray-500 bg-opacity-20 text-gray-400 border border-gray-500 border-opacity-30',
};

const PAYOUT_STATUS_ICONS: Record<string, string> = {
  pending: '⏳',
  processing: '🔄',
  paid: '✅',
  failed: '❌',
  cancelled: '🚫',
};

export default function CreatorPage() {
  const supabase = createClient();
  const { user, profile, loading: authLoading, signOut } = useAuth();
  const router = useRouter();

  const [videos, setVideos] = useState<CreatorVideo[]>([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Earnings state
  const [tips, setTips] = useState<TipRecord[]>([]);
  const [loadingTips, setLoadingTips] = useState(false);
  const [activeTab, setActiveTab] = useState<'videos' | 'earnings'>('videos');

  // Payouts state
  const [payouts, setPayouts] = useState<PayoutRecord[]>([]);
  const [loadingPayouts, setLoadingPayouts] = useState(false);
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutSubmitting, setPayoutSubmitting] = useState(false);
  const [payoutError, setPayoutError] = useState('');
  const [payoutSuccess, setPayoutSuccess] = useState('');

  const fetchMyVideos = useCallback(async () => {
    if (!user) return;
    setLoadingVideos(true);

    const email = user.email || '';
    const name = profile?.full_name || '';

    const { data, error } = await supabase
      .from('videos')
      .select('id, title, category, video_status, views, likes_count, comments_count, created_at, thumbnail, thumbnail_alt')
      .or(`submitted_by.eq.${email},submitted_by.eq.${name}`)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setVideos(data as CreatorVideo[]);
    }
    setLoadingVideos(false);
  }, [user, profile]);

  const fetchMyTips = useCallback(async () => {
    if (!user?.email) return;
    setLoadingTips(true);

    const { data, error } = await supabase
      .from('video_tips')
      .select('id, video_id, amount, payment_status, tipper_name, message, created_at')
      .eq('creator_email', user.email)
      .eq('payment_status', 'succeeded')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setTips(data as TipRecord[]);
    }
    setLoadingTips(false);
  }, [user]);

  const fetchMyPayouts = useCallback(async () => {
    if (!user?.email) return;
    setLoadingPayouts(true);

    const { data, error } = await supabase
      .from('creator_payouts')
      .select('id, amount, currency, status, stripe_transfer_id, stripe_payout_id, payout_method, notes, requested_at, processed_at, created_at')
      .eq('creator_email', user.email)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setPayouts(data as PayoutRecord[]);
    }
    setLoadingPayouts(false);
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchMyVideos();
      fetchMyTips();
      fetchMyPayouts();
    }
  }, [user, fetchMyVideos, fetchMyTips, fetchMyPayouts]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const totalViews = videos.reduce((sum, v) => sum + (v.views || 0), 0);
  const totalLikes = videos.reduce((sum, v) => sum + (v.likes_count || 0), 0);
  const approvedCount = videos.filter((v) => v.video_status === 'approved').length;

  // Earnings calculations
  const totalTipsEarned = tips.reduce((sum, t) => sum + (t.amount || 0), 0);

  // Payout calculations
  const totalPaidOut = payouts
    .filter((p) => p.status === 'paid')
    .reduce((sum, p) => sum + p.amount, 0);
  const pendingPayouts = payouts
    .filter((p) => p.status === 'pending' || p.status === 'processing')
    .reduce((sum, p) => sum + p.amount, 0);
  const availableBalance = totalTipsEarned - totalPaidOut - pendingPayouts;

  // Top performing videos by tips
  const videoTipMap: Record<string, VideoTipSummary> = {};
  tips.forEach((tip) => {
    if (!tip.video_id) return;
    if (!videoTipMap[tip.video_id]) {
      const video = videos.find((v) => v.id === tip.video_id);
      videoTipMap[tip.video_id] = {
        video_id: tip.video_id,
        title: video?.title || 'Unknown Video',
        thumbnail: video?.thumbnail || '',
        thumbnail_alt: video?.thumbnail_alt || '',
        total_tips: 0,
        tip_count: 0,
      };
    }
    videoTipMap[tip.video_id].total_tips += tip.amount;
    videoTipMap[tip.video_id].tip_count += 1;
  });
  const topVideos = Object.values(videoTipMap)
    .sort((a, b) => b.total_tips - a.total_tips)
    .slice(0, 5);

  // Daily earnings breakdown (last 30 days)
  const dailyMap: Record<string, DailyEarning> = {};
  tips.forEach((tip) => {
    const day = tip.created_at.slice(0, 10);
    if (!dailyMap[day]) {
      dailyMap[day] = { date: day, amount: 0, count: 0 };
    }
    dailyMap[day].amount += tip.amount;
    dailyMap[day].count += 1;
  });
  const dailyEarnings = Object.values(dailyMap)
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 30);

  const maxDailyAmount = dailyEarnings.length > 0 ? Math.max(...dailyEarnings.map((d) => d.amount)) : 1;

  const handleRequestPayout = async () => {
    setPayoutError('');
    setPayoutSuccess('');
    const amountDollars = parseFloat(payoutAmount);
    if (!payoutAmount || isNaN(amountDollars) || amountDollars < 10) {
      setPayoutError('Minimum withdrawal is $10.00.');
      return;
    }
    const amountCents = Math.round(amountDollars * 100);
    if (amountCents > availableBalance) {
      setPayoutError(`Amount exceeds available balance of ${formatCurrency(availableBalance)}.`);
      return;
    }
    setPayoutSubmitting(true);
    const { error } = await supabase.from('creator_payouts').insert({
      creator_email: user?.email,
      amount: amountCents,
      currency: 'usd',
      status: 'pending',
      payout_method: 'bank_transfer',
    });
    setPayoutSubmitting(false);
    if (error) {
      setPayoutError('Failed to submit withdrawal request. Please try again.');
    } else {
      setPayoutSuccess('Withdrawal request submitted! Our team will process it within 3–5 business days.');
      setPayoutAmount('');
      setShowPayoutForm(false);
      fetchMyPayouts();
    }
  };

  return (
    <div className="min-h-screen bg-surface-primary flex flex-col">
      <NavBar activePage="other" />

      <main className="flex-1 max-w-5xl mx-auto w-full px-3 sm:px-4 py-6 sm:py-8">
        {/* Page header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-1 h-6 bg-brand-red"></div>
            <h1 className="text-xl sm:text-2xl font-bold text-text-primary uppercase tracking-wide">Creator Account</h1>
          </div>
          <p className="text-text-secondary text-sm ml-3">Manage your submitted videos and track performance</p>
        </div>

        {authLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : !user ? (
          /* Not signed in */
          <div className="max-w-md mx-auto text-center py-12 sm:py-16 px-4">
            <div className="w-16 h-16 rounded-full bg-surface-secondary border border-stroke-primary flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-brand-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-text-primary mb-2">Creator Sign In</h2>
            <p className="text-text-secondary text-sm mb-6">
              Sign in or create a free creator account with your email to manage your submitted videos and track views, likes, and comments.
            </p>
            <button
              onClick={() => setShowAuthModal(true)}
              className="btn-primary px-8 py-3 text-base"
            >
              Sign In / Create Account
            </button>
            <p className="text-text-muted text-xs mt-4">
              Only your email is required — no social login needed.
            </p>
          </div>
        ) : (
          /* Signed in — creator dashboard */
          <div>
            {/* Profile card */}
            <div className="bg-surface-secondary border border-stroke-primary rounded-lg p-4 sm:p-5 mb-5 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-brand-red flex items-center justify-center text-white text-lg sm:text-xl font-bold flex-shrink-0">
                  {(profile?.full_name || user.email || 'C')[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-text-primary font-bold text-base sm:text-lg">
                    {profile?.full_name || user.email?.split('@')[0] || 'Creator'}
                  </p>
                  <p className="text-text-muted text-xs sm:text-sm">{user.email}</p>
                </div>
              </div>
              <button
                onClick={async () => { await signOut(); router.push('/'); }}
                className="text-text-secondary hover:text-text-primary text-sm border border-stroke-primary rounded-[4px] px-3 py-1.5 transition-colors self-start sm:self-auto"
              >
                Sign Out
              </button>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 mb-5 sm:mb-6">
              {[
                { label: 'Videos', value: videos.length },
                { label: 'Approved', value: approvedCount },
                { label: 'Total Views', value: totalViews.toLocaleString() },
                { label: 'Total Likes', value: totalLikes.toLocaleString() },
                { label: 'Tips Earned', value: formatCurrency(totalTipsEarned), highlight: true },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className={`border rounded-lg p-3 sm:p-4 text-center ${
                    stat.highlight
                      ? 'bg-brand-red bg-opacity-10 border-brand-red border-opacity-30' :'bg-surface-secondary border-stroke-primary'
                  }`}
                >
                  <p className={`text-xl sm:text-2xl font-bold ${stat.highlight ? 'text-brand-red' : 'text-text-primary'}`}>
                    {stat.value}
                  </p>
                  <p className="text-text-muted text-xs uppercase tracking-wide mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-4 border-b border-stroke-primary overflow-x-auto">
              <button
                onClick={() => setActiveTab('videos')}
                className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
                  activeTab === 'videos' ?'border-brand-red text-brand-red' :'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                My Videos
              </button>
              <button
                onClick={() => setActiveTab('earnings')}
                className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-1.5 whitespace-nowrap ${
                  activeTab === 'earnings' ?'border-brand-red text-brand-red' :'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                💸 Earnings & Payouts
                {tips.length > 0 && (
                  <span className="bg-brand-red text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
                    {tips.length}
                  </span>
                )}
              </button>
            </div>

            {/* Videos Tab */}
            {activeTab === 'videos' && (
              <div className="bg-surface-secondary border border-stroke-primary rounded-lg overflow-hidden">
                <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-stroke-primary flex items-center justify-between">
                  <h2 className="text-text-primary font-bold uppercase tracking-wide text-sm">My Submitted Videos</h2>
                  <Link href="/" className="text-brand-red text-sm hover:underline font-medium">
                    + Submit New
                  </Link>
                </div>

                {loadingVideos ? (
                  <div className="flex items-center justify-center py-12 gap-2 text-text-muted">
                    <div className="w-5 h-5 border-2 border-text-muted border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm">Loading your videos...</span>
                  </div>
                ) : videos.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <svg className="w-12 h-12 text-text-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <p className="text-text-secondary font-medium mb-1">No videos found</p>
                    <p className="text-text-muted text-sm">
                      Videos you submit using this email address will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-stroke-primary">
                    {videos.map((video) => (
                      <div key={video.id} className="flex items-start gap-3 p-3 sm:p-4 hover:bg-surface-tertiary transition-colors">
                        {/* Thumbnail */}
                        <div className="w-20 sm:w-24 h-12 sm:h-14 rounded overflow-hidden flex-shrink-0 bg-surface-tertiary">
                          {video.thumbnail ? (
                            <img
                              src={video.thumbnail}
                              alt={video.thumbnail_alt || video.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg className="w-6 h-6 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <Link
                              href={`/video/${video.id}`}
                              className="text-text-primary font-medium text-sm hover:text-brand-red transition-colors line-clamp-2 flex-1 min-w-0"
                            >
                              {video.title}
                            </Link>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_COLORS[video.video_status] || STATUS_COLORS.pending}`}>
                              {video.video_status}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-3 mt-1.5 text-text-muted text-xs flex-wrap">
                            {video.category && <span className="hidden sm:inline">{video.category}</span>}
                            <span>{formatDate(video.created_at)}</span>
                            <span className="flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              {(video.views || 0).toLocaleString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                              </svg>
                              {(video.likes_count || 0).toLocaleString()}
                            </span>
                            {videoTipMap[video.id] && (
                              <span className="flex items-center gap-1 text-brand-red font-medium">
                                💸 {formatCurrency(videoTipMap[video.id].total_tips)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Earnings Tab */}
            {activeTab === 'earnings' && (
              <div className="space-y-4 sm:space-y-5">
                {loadingTips ? (
                  <div className="flex items-center justify-center py-16 gap-2 text-text-muted">
                    <div className="w-5 h-5 border-2 border-text-muted border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm">Loading earnings...</span>
                  </div>
                ) : (
                  <>
                    {/* ── PAYOUT BALANCE CARD ── */}
                    <div className="bg-surface-secondary border border-stroke-primary rounded-lg overflow-hidden">
                      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-stroke-primary flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <h2 className="text-text-primary font-bold uppercase tracking-wide text-sm">💳 Payout Balance</h2>
                          <p className="text-text-muted text-xs mt-0.5">Stripe-powered withdrawals</p>
                        </div>
                        {availableBalance >= 1000 && (
                          <button
                            onClick={() => { setShowPayoutForm((v) => !v); setPayoutError(''); setPayoutSuccess(''); }}
                            className="btn-primary text-xs px-3 py-1.5 flex-shrink-0"
                          >
                            {showPayoutForm ? 'Cancel' : '+ Request Withdrawal'}
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-stroke-primary">
                        <div className="p-4 sm:p-5 text-center">
                          <p className="text-2xl sm:text-3xl font-bold text-green-400">{formatCurrency(availableBalance)}</p>
                          <p className="text-text-muted text-xs uppercase tracking-wide mt-1">Available to Withdraw</p>
                        </div>
                        <div className="p-4 sm:p-5 text-center">
                          <p className="text-2xl sm:text-3xl font-bold text-yellow-400">{formatCurrency(pendingPayouts)}</p>
                          <p className="text-text-muted text-xs uppercase tracking-wide mt-1">Pending Payouts</p>
                        </div>
                        <div className="p-4 sm:p-5 text-center">
                          <p className="text-2xl sm:text-3xl font-bold text-text-primary">{formatCurrency(totalPaidOut)}</p>
                          <p className="text-text-muted text-xs uppercase tracking-wide mt-1">Total Paid Out</p>
                        </div>
                      </div>

                      {/* Minimum notice */}
                      {availableBalance < 1000 && availableBalance > 0 && (
                        <div className="px-4 sm:px-5 py-3 border-t border-stroke-primary bg-yellow-500 bg-opacity-5">
                          <p className="text-yellow-400 text-xs flex items-center gap-1.5">
                            <span>⚠️</span>
                            Minimum withdrawal is $10.00. You need {formatCurrency(1000 - availableBalance)} more to request a payout.
                          </p>
                        </div>
                      )}

                      {/* Withdrawal form */}
                      {showPayoutForm && (
                        <div className="px-4 sm:px-5 py-4 border-t border-stroke-primary bg-surface-tertiary">
                          <p className="text-text-primary font-medium text-sm mb-3">Request Withdrawal</p>
                          <div className="flex gap-2 flex-wrap">
                            <div className="relative flex-1 min-w-[140px]">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm">$</span>
                              <input
                                type="number"
                                min="10"
                                step="0.01"
                                placeholder="Amount"
                                value={payoutAmount}
                                onChange={(e) => setPayoutAmount(e.target.value)}
                                className="w-full bg-surface-secondary border border-stroke-primary rounded-lg pl-7 pr-3 py-2 text-text-primary text-sm focus:outline-none focus:border-brand-red"
                              />
                            </div>
                            <button
                              onClick={handleRequestPayout}
                              disabled={payoutSubmitting}
                              className="btn-primary text-sm px-4 py-2 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
                            >
                              {payoutSubmitting ? (
                                <>
                                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  Submitting…
                                </>
                              ) : 'Submit Request'}
                            </button>
                          </div>
                          {payoutError && <p className="text-red-400 text-xs mt-2">{payoutError}</p>}
                          {payoutSuccess && <p className="text-green-400 text-xs mt-2">{payoutSuccess}</p>}
                          <p className="text-text-muted text-xs mt-2">
                            Payouts are processed via Stripe within 3–5 business days to your registered bank account.
                          </p>
                        </div>
                      )}

                      {payoutSuccess && !showPayoutForm && (
                        <div className="px-4 sm:px-5 py-3 border-t border-stroke-primary bg-green-500 bg-opacity-5">
                          <p className="text-green-400 text-xs">{payoutSuccess}</p>
                        </div>
                      )}
                    </div>

                    {/* ── PAYOUT HISTORY ── */}
                    <div className="bg-surface-secondary border border-stroke-primary rounded-lg overflow-hidden">
                      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-stroke-primary flex items-center justify-between">
                        <div>
                          <h2 className="text-text-primary font-bold uppercase tracking-wide text-sm">🏦 Withdrawal History</h2>
                          <p className="text-text-muted text-xs mt-0.5">All payout requests and their Stripe status</p>
                        </div>
                        {loadingPayouts && (
                          <div className="w-4 h-4 border-2 border-text-muted border-t-transparent rounded-full animate-spin"></div>
                        )}
                      </div>

                      {!loadingPayouts && payouts.length === 0 ? (
                        <div className="text-center py-10 px-4">
                          <div className="text-3xl mb-2">🏦</div>
                          <p className="text-text-secondary font-medium text-sm mb-1">No withdrawals yet</p>
                          <p className="text-text-muted text-xs">
                            Once you reach $10.00 in earnings, you can request a payout.
                          </p>
                        </div>
                      ) : (
                        <div className="divide-y divide-stroke-primary">
                          {payouts.map((payout) => (
                            <div key={payout.id} className="flex items-center gap-3 p-3 sm:p-4 hover:bg-surface-tertiary transition-colors">
                              <div className="w-8 h-8 rounded-full bg-surface-tertiary flex items-center justify-center flex-shrink-0 text-base">
                                {PAYOUT_STATUS_ICONS[payout.status] || '💳'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-text-primary font-medium text-sm">
                                    {formatCurrency(payout.amount)} withdrawal
                                  </p>
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${PAYOUT_STATUS_STYLES[payout.status] || PAYOUT_STATUS_STYLES.pending}`}>
                                    {payout.status}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                                  <p className="text-text-muted text-xs">
                                    Requested {formatDate(payout.requested_at)}
                                  </p>
                                  {payout.processed_at && (
                                    <p className="text-text-muted text-xs">
                                      · Processed {formatDate(payout.processed_at)}
                                    </p>
                                  )}
                                  {payout.payout_method && (
                                    <p className="text-text-muted text-xs capitalize">
                                      · {payout.payout_method.replace('_', ' ')}
                                    </p>
                                  )}
                                </div>
                                {(payout.stripe_transfer_id || payout.stripe_payout_id) && (
                                  <p className="text-text-muted text-xs mt-0.5 font-mono truncate">
                                    Stripe: {payout.stripe_transfer_id || payout.stripe_payout_id}
                                  </p>
                                )}
                                {payout.notes && (
                                  <p className="text-text-secondary text-xs mt-0.5 italic">{payout.notes}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* ── TIP REVENUE SUMMARY ── */}
                    {tips.length === 0 ? (
                      <div className="bg-surface-secondary border border-stroke-primary rounded-lg text-center py-16 px-4">
                        <div className="text-4xl mb-3">💸</div>
                        <p className="text-text-secondary font-medium mb-1">No tips yet</p>
                        <p className="text-text-muted text-sm">
                          When viewers tip your videos, your earnings will appear here.
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Total tips summary */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                          <div className="bg-brand-red bg-opacity-10 border border-brand-red border-opacity-30 rounded-lg p-4 sm:p-5 text-center">
                            <p className="text-2xl sm:text-3xl font-bold text-brand-red">{formatCurrency(totalTipsEarned)}</p>
                            <p className="text-text-muted text-xs uppercase tracking-wide mt-1">Total Tips Earned</p>
                          </div>
                          <div className="bg-surface-secondary border border-stroke-primary rounded-lg p-4 sm:p-5 text-center">
                            <p className="text-2xl sm:text-3xl font-bold text-text-primary">{tips.length}</p>
                            <p className="text-text-muted text-xs uppercase tracking-wide mt-1">Total Tips Received</p>
                          </div>
                          <div className="bg-surface-secondary border border-stroke-primary rounded-lg p-4 sm:p-5 text-center">
                            <p className="text-2xl sm:text-3xl font-bold text-text-primary">
                              {tips.length > 0 ? formatCurrency(Math.round(totalTipsEarned / tips.length)) : '$0.00'}
                            </p>
                            <p className="text-text-muted text-xs uppercase tracking-wide mt-1">Avg Tip Amount</p>
                          </div>
                        </div>

                        {/* Top performing videos */}
                        {topVideos.length > 0 && (
                          <div className="bg-surface-secondary border border-stroke-primary rounded-lg overflow-hidden">
                            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-stroke-primary">
                              <h2 className="text-text-primary font-bold uppercase tracking-wide text-sm">🏆 Top Tipped Videos</h2>
                            </div>
                            <div className="divide-y divide-stroke-primary">
                              {topVideos.map((v, idx) => (
                                <div key={v.video_id} className="flex items-center gap-3 p-3 sm:p-4 hover:bg-surface-tertiary transition-colors">
                                  <span className="text-text-muted text-sm font-bold w-5 text-center flex-shrink-0">
                                    {idx + 1}
                                  </span>
                                  {v.thumbnail ? (
                                    <img
                                      src={v.thumbnail}
                                      alt={v.thumbnail_alt || v.title}
                                      className="w-14 sm:w-16 h-9 sm:h-10 rounded object-cover flex-shrink-0"
                                    />
                                  ) : (
                                    <div className="w-14 sm:w-16 h-9 sm:h-10 rounded bg-surface-tertiary flex items-center justify-center flex-shrink-0">
                                      <svg className="w-5 h-5 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                      </svg>
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <Link
                                      href={`/video/${v.video_id}`}
                                      className="text-text-primary text-sm font-medium hover:text-brand-red transition-colors line-clamp-1"
                                    >
                                      {v.title}
                                    </Link>
                                    <p className="text-text-muted text-xs mt-0.5">{v.tip_count} tip{v.tip_count !== 1 ? 's' : ''}</p>
                                  </div>
                                  <div className="text-right flex-shrink-0">
                                    <p className="text-brand-red font-bold text-sm">{formatCurrency(v.total_tips)}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Earnings by date */}
                        {dailyEarnings.length > 0 && (
                          <div className="bg-surface-secondary border border-stroke-primary rounded-lg overflow-hidden">
                            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-stroke-primary">
                              <h2 className="text-text-primary font-bold uppercase tracking-wide text-sm">📅 Earnings by Date</h2>
                              <p className="text-text-muted text-xs mt-0.5">Last 30 days of activity</p>
                            </div>
                            <div className="divide-y divide-stroke-primary">
                              {dailyEarnings.map((day) => (
                                <div key={day.date} className="flex items-center gap-2 sm:gap-4 px-3 sm:px-5 py-2.5 sm:py-3">
                                  <div className="w-20 sm:w-28 flex-shrink-0">
                                    <p className="text-text-secondary text-xs sm:text-sm">
                                      {new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                      })}
                                    </p>
                                  </div>
                                  {/* Bar chart */}
                                  <div className="flex-1 h-4 sm:h-5 bg-surface-tertiary rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-brand-red rounded-full transition-all"
                                      style={{ width: `${Math.max(4, (day.amount / maxDailyAmount) * 100)}%` }}
                                    />
                                  </div>
                                  <div className="w-16 sm:w-20 text-right flex-shrink-0">
                                    <p className="text-text-primary font-bold text-xs sm:text-sm">{formatCurrency(day.amount)}</p>
                                    <p className="text-text-muted text-xs">{day.count} tip{day.count !== 1 ? 's' : ''}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Recent tips list */}
                        <div className="bg-surface-secondary border border-stroke-primary rounded-lg overflow-hidden">
                          <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-stroke-primary">
                            <h2 className="text-text-primary font-bold uppercase tracking-wide text-sm">Recent Tips</h2>
                          </div>
                          <div className="divide-y divide-stroke-primary">
                            {tips.slice(0, 20).map((tip) => {
                              const video = videos.find((v) => v.id === tip.video_id);
                              return (
                                <div key={tip.id} className="flex items-start gap-3 p-3 sm:p-4 hover:bg-surface-tertiary transition-colors">
                                  <div className="w-8 h-8 rounded-full bg-brand-red bg-opacity-15 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-sm">💸</span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                      <p className="text-text-primary text-sm font-medium">
                                        {tip.tipper_name || 'Anonymous'}
                                      </p>
                                      <p className="text-brand-red font-bold text-sm flex-shrink-0">
                                        {formatCurrency(tip.amount)}
                                      </p>
                                    </div>
                                    {video && (
                                      <Link
                                        href={`/video/${video.id}`}
                                        className="text-text-muted text-xs hover:text-brand-red transition-colors line-clamp-1"
                                      >
                                        on: {video.title}
                                      </Link>
                                    )}
                                    {tip.message && (
                                      <p className="text-text-secondary text-xs mt-1 italic line-clamp-2">"{tip.message}"</p>
                                    )}
                                    <p className="text-text-muted text-xs mt-1">{formatDate(tip.created_at)}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      <HomeFooter />

      <EmailAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => setShowAuthModal(false)}
      />
    </div>
  );
}
